import moment from 'moment';

import config from '../config';
const CONFIG_PLANS = config.stripe.plans;

const resolvePlanDetails = ({ user, organization }) => {
  const plan = (organization ? organization?.organization?.planId : user?.plan);
  const appSumoCodes = (organization ? organization?.appSumoCodes : user?.appSumoCodes || []);
  return {
    plan,
    appSumoCodes: appSumoCodes || [],
  }
}

/**
 * Check if user is enterprise.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isEnterprise = ({ user, organization }) => {
  const planDetails = resolvePlanDetails({ user, organization })
  return isEnterpriseMonthly(planDetails.plan) || isEnterpriseAnnually(planDetails.plan);
}

/**
 * Check if user is enterprise with a monthly plan.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isEnterpriseMonthly = (plan) =>
  plan === CONFIG_PLANS.enterprise.planId ||
  plan === "enterprise" ||
  plan === "enterprise-monthly" ||
  plan === CONFIG_PLANS.enterpriseMonthly.planId;

/**
 * Check if user is enterprise with a yearly plan.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isEnterpriseAnnually = (plan) =>
  plan === "enterprise-annually" ||
  plan === CONFIG_PLANS.enterpriseAnnually.planId;

/**
 * Check if user is premium.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isPremium = ({ user, organization }) => {
  const planDetails = resolvePlanDetails({ user, organization });
  return isPremiumMonthly(planDetails.plan) ||
    isPremiumAnnually(planDetails.plan);
}

/**
 * Check if user is premium with a monthly plan.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isPremiumMonthly = (plan) =>
  plan === "premium" ||
  plan === CONFIG_PLANS.premium.planId ||
  (CONFIG_PLANS.premium.oldPlansId && CONFIG_PLANS.premium.oldPlansId.includes(plan));


/**
 * Check if user is premium with a yearly plan.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isPremiumAnnually = (plan) =>
  plan === "premium-annually" ||
  plan === CONFIG_PLANS.premiumAnnually.planId;

/**
 * Check if user has redeemed 3 or more AppSumo codes
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 */
export const isAppSumo3OrMore = ({ user, organization }) => {
  const planDetails = resolvePlanDetails({ user, organization });
  return isAppsumo({ user, organization }) && planDetails.appSumoCodes
    ? planDetails.appSumoCodes.length > 2
    : false;
}

/**
 * Check if user has an appsumo plan
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 */
export const isAppsumo = ({ user, organization }) => {
  const planDetails = resolvePlanDetails({ user, organization });
  return planDetails.plan === "appsumo";
}

/**
 * Check if user is publisher
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 */
export const isPublisher = ({ user, organization }) => {
  const planDetails = resolvePlanDetails({ user, organization });
  return planDetails.plan === "publisher" ||
    planDetails.plan === "founderBasic" ||
    planDetails.plan === "editor";
}

/**
 * Check if user is pro.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isPro = ({ user, organization }) => {
  const planDetails = resolvePlanDetails({ user, organization });
  return isProMonthly(planDetails.plan) ||
    isProAnnually(planDetails.plan);
}
/**
 * Check if user is pro with a monthly plan.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isProMonthly = (plan) => {
  const result = plan === "pro" ||
  plan === CONFIG_PLANS.pro.planId ||
  (CONFIG_PLANS.pro.oldPlansId ? CONFIG_PLANS.pro.oldPlansId.includes(plan) : false);
  return result;
}

/**
 * Check if user is pro with a yearly plan.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isProAnnually = (plan) =>
  plan === "pro_annual" ||
  plan === "pro-annually" ||
  plan === CONFIG_PLANS.proAnnually.planId;

/**
 * Check if user is free.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isFree = ({ user, organization }) => {
  const planDetails = resolvePlanDetails({ user, organization });
  return planDetails.plan === CONFIG_PLANS.basic.planId ||
    planDetails.plan === "basic" ||
    planDetails.plan === "free";
}

/**
 * Check if user is amazon basic.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isAmazonBasic = ({ user, organization }) => {
  return isAmazonBasicMonthly({ user, organization }) ||
    isAmazonBasicAnnually({ user, organization });
}

/**
 * Check if user is amazon basic with a monthly plan.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isAmazonBasicMonthly = ({ user, organization }) => {
  const planDetails = resolvePlanDetails({ user, organization });
  return planDetails.plan === CONFIG_PLANS.amazonBasicMonthly.planId;
}

/**
 * Check if user is amazon basic with a yearly plan.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isAmazonBasicAnnually = ({ user, organization }) => {
  const planDetails = resolvePlanDetails({ user, organization });
  return planDetails.plan === CONFIG_PLANS.amazonBasicAnnual.planId;
}

/**
 * Check if user is amazon Premium.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isAmazonPremium = ({ user, organization }) => {
  return isAmazonPremiumMonthly({ user, organization }) ||
    isAmazonPremiumAnnually({ user, organization });
}

/**
 * Check if user is amazon premium with a monthly plan.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isAmazonPremiumMonthly = ({ user, organization }) => {
  const planDetails = resolvePlanDetails({ user, organization });
  return planDetails.plan === CONFIG_PLANS.amazonPremiumMonthly.planId;
}

/**
 * Check if user is amazon premium with a yearly plan.
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const isAmazonPremiumAnnually = ({ user, organization }) => {
  const planDetails = resolvePlanDetails({ user, organization });
  return planDetails.plan === CONFIG_PLANS.amazonPremiumAnnual.planId;
}

/**
 * Returns how many monthly videos user has access to
 * @constructor
 * @param {string} user - User object from the auth Store, which fetches from /session endpoint.
 * @param {string} planConfig - Plans configuration file for the environment.
 */
export const getUserMonthlyVideoLimit = ({ user, organization }) => {
  if (isPro({ user, organization })) {
    const getDateMoment = date => moment(moment(date).format('DD/MM/YYYY'), 'DD/MM/YYYY');

    const proLimitDebut = getDateMoment(CONFIG_PLANS.pro.perMonthVideo.debut);
    const proLimit = CONFIG_PLANS.pro.perMonthVideo.limit;
    const userPlanAccessionDate = getDateMoment(user.subscriptionAccessionDate);

    return userPlanAccessionDate >= proLimitDebut ? proLimit : config.perMonthVideo;
  } else if (isAppsumo({ user, organization })) {
    const appliedCodes = user.appSumoCodes.length;

    // progression per code is 50, 100, 200, 250, 300, 350...
    // notice how most increments are 50 except from 100-200 on the 3rd code. I think this was done to get people to
    // try to buy 3 or more codes and now we get to pay the price with implementation...
    return config.perMonthVideo * appliedCodes + ((appliedCodes >= 3) ? config.perMonthVideo : 0);
  } else if (isAmazonBasic({ user, organization })) {
    return CONFIG_PLANS.amazonBasicMonthly.perMonthVideo.limit;
  }

  return null;
}

export const getFormattedPlanName = (user, organization) => {
  if (isFree({ user, organization })) {
    return 'Free';
  }
  if (isPro({ user, organization })) {
    return 'Pro';
  }
  if (isPremium({ user, organization })) {
    return 'Premium';
  }
  if (isEnterprise({ user, organization })) {
    return 'Enterprise';
  }
  if (isAppsumo({ user, organization })) {
    return 'AppSumo';
  }
  if (isAmazonBasic({ user, organization })) {
    return 'AmazonBasic'
  }
  if (isAmazonPremium({ user, organization })) {
    return 'AmazonPremium'
  }
  return '';
}



// WEBPACK FOOTER //
// ./src/helpers/plansHelper.js